// Generated by outlaw-generator. Change the generator or schema inputs.
import util from '../../lib/util';
import {tabSession} from '../../Auth/session';
import {workingSet} from './mergeContext';
import {DELETE_QUEUE_FAILED_MESSAGE_KEY} from '../../lib/alertMessages';
export {DELETE_QUEUE_FAILED_MESSAGE_KEY} from '../../lib/alertMessages';

/** True only after this tab has fetched all mutable collections in its current session. */
export function canCascadeDelete(): boolean {
    return tabSession.hasFetchedEntities(['accountingConnection', 'activityCategory', 'activityType', 'address', 'answer', 'bankAccount', 'blackout', 'category', 'campaign', 'docket', 'matter', 'comment', 'caseType', 'caseTypeStage', 'deadline', 'deadlineRule', 'conflict', 'contact', 'court', 'courtDeadlineRule', 'discount', 'entityTimestamp', 'document', 'expense', 'event', 'firm', 'flatFee', 'flatFeeMilestone', 'group', 'holidayCalendar', 'identification', 'importExport', 'importSource', 'invoice', 'invoiceBatch', 'invoiceBatchItem', 'lead', 'lookup', 'matterStage', 'milestone', 'option', 'participant', 'privilege', 'question', 'redFlagType', 'redFlag', 'resource', 'referral', 'role', 'task', 'taskSeries', 'templateTask', 'timeOff', 'transaction', 'user']);
}

export type DeleteImpact = {value: string, count: number}[];
/** deleted means accepted synchronously; completion reports whether queue persistence succeeded. */
export type DeleteResult = {status: 'deleted' | 'blocked' | 'notReady', impacts: DeleteImpact, completion?: Promise<boolean>};
export type DeleteImpactContext = {visited: Set<string>, impacts: Map<string, string>, blocked: boolean};
type DeleteAcknowledgement = {saved?: boolean, waiting: ((saved: boolean) => void)[], notice: {reported: boolean, partialSuccess: boolean}};
export type DeleteTraversal = {visited: Set<string>, importSource: 'delete' | 'merge', importId: string};
const deleteQueues = new WeakMap<Map<string, unknown>, DeleteAcknowledgement>();

/** Include new working rows and evaluate predicates against the latest version. */
export function workingRows<T extends {id: string, status?: string}>(entity: string, rows: {map<U>(fn: (row: T) => U): U[]}): T[] {
    const current = new Map<string, T>(rows.map(row => [row.id, row] as [string, T]));
    for (const [key, value] of workingSet()) {
        if (key.startsWith(entity + ':')) {
            const row = value as T;
            current.set(row.id, row);
        }
    }
    return [...current.values()].filter(row => row.status !== 'D');
}

/** Queue each descendant durably before allowing the next row or its parent to enqueue. */
export function enqueueDelete(work: Map<string, unknown>, send: (onQueued: (saved: boolean, deleted?: boolean) => void) => void, failed: () => void): Promise<boolean> {
    const session = tabSession.captureGeneration();
    const previous = deleteQueues.get(work);
    // Separate roots in this synchronous working set share both ordering and one failure notice.
    const acknowledgement: DeleteAcknowledgement = {waiting: [], notice: previous?.notice ?? {reported: false, partialSuccess: false}};
    deleteQueues.set(work, acknowledgement);
    let resolve!: (saved: boolean) => void;
    const completion = new Promise<boolean>(done => { resolve = done; });
    const report = (saved: boolean, deleted = false) => {
        if (acknowledgement.saved !== undefined) return;
        acknowledgement.saved = saved;
        if (saved && deleted) acknowledgement.notice.partialSuccess = true;
        resolve(saved);
        if (!saved) {
            failed();
            if (!acknowledgement.notice.reported && !session.signal.aborted) {
                acknowledgement.notice.reported = true;
                // Loading alerts must not pull the application store into readiness consumers.
                void import('../../lib/alert').then(({default: Alert}) => {
                    if (session.signal.aborted) return;
                    const alert = new Alert(Alert.WARNING, crypto.randomUUID(), 0, DELETE_QUEUE_FAILED_MESSAGE_KEY);
                    alert.deleteQueuePartialSuccess = acknowledgement.notice.partialSuccess;
                    Alert.add(alert);
                }).catch(err => console.error(err));
            }
        }
        for (const waiting of acknowledgement.waiting.splice(0)) waiting(saved);
    };
    const run = (saved: boolean) => {
        if (!saved) { report(false); return; }
        try { send(report); }
        catch (err) { report(false); throw err; }
    };
    if (previous && previous.saved === undefined) previous.waiting.push(run);
    else run(previous?.saved ?? true);
    return completion;
}

export function deleteImpactContext(): DeleteImpactContext {
    return {visited: new Set(), impacts: new Map(), blocked: false};
}

export function addDeleteImpact(context: DeleteImpactContext, key: string, value: string): void {
    if (!context.impacts.get(key)?.endsWith('*')) context.impacts.set(key, value);
    context.blocked ||= value.endsWith('*');
}

export function deleteImpactCounts(context: DeleteImpactContext): DeleteImpact {
    return util.countDistinct([...context.impacts.values()]);
}
